import { findBrandByDomain } from "../../config/brands.ts";
import brandDictionary from "../../config/brand-dictionary.json";

const LOGIN_API_PATH = "/api/v1/user/login";
const GENERIC_AUTH_KEY = "api_auth_token";
const OWN_KEY_PREFIXES = ["auth_token_", GENERIC_AUTH_KEY, "api_test_var_"];

// Comprueba si una URL corresponde al login (se excluye de la inyección de token)
function isLoginPath(pathname: string): boolean {
  return pathname.toLowerCase().includes(LOGIN_API_PATH);
}

// Inyecta el Bearer guardado salvo en login o si ya existe Authorization
function withSavedAuth(headers: Headers, pathname: string): Headers {
  const token = getSavedAuthToken();
  if (token && !isLoginPath(pathname) && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  return headers;
}

// Asegura Content-Type JSON en peticiones con cuerpo
function ensureJsonHeader(headers: Headers, method?: string): void {
  const normalized = (method || "GET").toUpperCase();
  if ((normalized === "POST" || normalized === "PUT" || normalized === "PATCH") && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
}

// Clave de almacenamiento particionada por marca para aislar sesiones white-label 
export function getAuthStorageKey(hostname = typeof window !== "undefined" ? window.location.hostname : ""): string {
  try {
    const activeBrand = findBrandByDomain(hostname);
    return activeBrand?.id ? `auth_token_${activeBrand.id}` : "api_auth_token";
  } catch {
    return "api_auth_token";
  }
}

// Números de prueba (to/from) persistidos entre peticiones del playground
export function getSavedTestVariable(name: "to" | "from"): string | null {
  try {
    return localStorage.getItem(`api_test_var_${name}`) || null;
  } catch {
    return null;
  }
}

export function saveTestVariable(name: "to" | "from", value: string): void {
  try {
    if (value && !value.includes("{{") && !value.includes("}}")) {
      localStorage.setItem(`api_test_var_${name}`, value.trim());
    }
  } catch {
    // ignored
  }
}

// Resuelve variables estilo Postman ({{from}}, {{to}}, {{sender}}) en el cuerpo
export function resolveTemplateVariablesInBody(bodyText: string, hostname = typeof window !== "undefined" ? window.location.hostname : ""): string {
  try {
    const savedFrom = getSavedTestVariable("from") || "00000";
    const savedTo = getSavedTestVariable("to") || "525512345678";

    try {
      const parsed = JSON.parse(bodyText);
      if (parsed && typeof parsed === "object") {
        if (typeof parsed.to === "string" && !parsed.to.includes("{{")) {
          saveTestVariable("to", parsed.to);
        }
        if (typeof parsed.from === "string" && !parsed.from.includes("{{")) {
          saveTestVariable("from", parsed.from);
        }
      }
    } catch {
      //ignored
    }

    let resolved = bodyText;
    resolved = resolved.replace(/\{\{\s*from\s*\}\}/g, savedFrom);
    resolved = resolved.replace(/\{\{\s*sender\s*\}\}/g, savedFrom);
    resolved = resolved.replace(/\{\{\s*to\s*\}\}/g, savedTo);

    return resolved;
  } catch {
    return bodyText;
  }
}

// Guarda el token en localStorage y en el store interno de Zudoku
export function saveAuthToken(token: string): void {
  try {
    localStorage.setItem(getAuthStorageKey(), token);
    const raw = sessionStorage.getItem("zudoku-security-credentials");
    const current = raw ? JSON.parse(raw) : { state: { credentials: {} }, version: 0 };
    const creds = current.state?.credentials || {};
    creds["BearerAuth"] = { value: token, isAuthorized: true };
    creds["bearerAuth"] = { value: token, isAuthorized: true };
    current.state = { ...current.state, credentials: creds };
    sessionStorage.setItem("zudoku-security-credentials", JSON.stringify(current));
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("api_auth_changed", { detail: { token } }));
    }
  } catch {
    // ignored
  }
}

// Limpia solo tokens propios en localStorage y sessionStorage
export function clearAuthToken(): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && OWN_KEY_PREFIXES.some((p) => key.startsWith(p))) {
        localStorage.removeItem(key);
      }
    }
    sessionStorage.removeItem("zudoku-security-credentials");
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("api_auth_changed", { detail: { token: null } }));
    }
  } catch {
    // ignored
  }
}

// Indica si un JWT expiró según su claim `exp`
export function isJwtExpired(token: string): boolean {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return false;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    const parsed = JSON.parse(jsonPayload);
    if (parsed && typeof parsed.exp === "number") {
      return Date.now() >= parsed.exp * 1000;
    }
  } catch {
    //ignored
  }
  return false;
}

/**
 * Recupera el token guardado (marca actual o genérico)
 * Limpia e invalida automáticamente el token si expiró
 */
export function getSavedAuthToken(): string | null {
  try {
    const checkToken = (token: string | null): string | null => {
      if (!token || token.trim().length === 0) return null;
      const trimmed = token.trim();
      if (isJwtExpired(trimmed)) {
        clearAuthToken();
        return null;
      }
      return trimmed;
    };

    const key = getAuthStorageKey();
    const fromBrand = checkToken(localStorage.getItem(key));
    if (fromBrand) return fromBrand;

    const fromGeneric = checkToken(localStorage.getItem(GENERIC_AUTH_KEY));
    if (fromGeneric) return fromGeneric;

    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith("auth_token_")) {
        const val = checkToken(localStorage.getItem(k));
        if (val) return val;
      }
    }

    const raw = sessionStorage.getItem("zudoku-security-credentials");
    if (raw) {
      const parsed = JSON.parse(raw);
      const val =
        parsed.state?.credentials?.BearerAuth?.value ||
        parsed.state?.credentials?.bearerAuth?.value;
      if (val && typeof val === "string") {
        return checkToken(val);
      }
    }
  } catch {
    //ignored
  }
  return null;
}

// Lista los servidores API configurados en el diccionario de marcas
export function getConfiguredApiServers(): string[] {
  try {
    const servers = new Set<string>();
    for (const [id, entry] of Object.entries(brandDictionary)) {
      if (id.startsWith("_")) continue;
      const b = entry as { apiServer?: string };
      if (b.apiServer) {
        servers.add(b.apiServer.replace(/\/+$/, ""));
      }
    }
    return Array.from(servers);
  } catch {
    return [];
  }
}

/**
 * Convierte una URL absoluta del gateway a ruta proxy same-origin
 * (/brand-api/<marca>/api/v1/...) para evitar CORS en hosts estáticos
 * Devuelve null si la URL no es una llamada conocida al gateway
 */
export function toBrandProxyPath(absoluteUrl: string): string | null {
  let target: URL;
  try {
    target = new URL(absoluteUrl);
  } catch {
    return null;
  }
  if (!target.pathname.startsWith("/api/v1/")) return null;
  const matchId = Object.entries(brandDictionary).find(([id, entry]) => {
    if (id.startsWith("_")) return false;
    const server = (entry as { apiServer?: string }).apiServer?.replace(/\/+$/, "");
    if (!server) return false;
    try {
      return new URL(server).origin === target.origin;
    } catch {
      return false;
    }
  })?.[0];
  if (!matchId) return null;
  return `/brand-api/${matchId}${target.pathname}${target.search}${target.hash}`;
}

// Extrae el token de una respuesta de login sin conocer su forma exacta
export function extractLoginToken(json: unknown): string | null {
  if (!json || typeof json !== "object") {
    return typeof json === "string" && json.trim().length > 0 ? json.trim() : null;
  }
  const obj = json as Record<string, unknown>;
  for (const key of ["data", "token", "accessToken"]) {
    const val = obj[key];
    if (typeof val === "string" && val.trim().length > 0) return val.trim();
  }
  const data = obj.data;
  if (data && typeof data === "object") {
    const nested = data as Record<string, unknown>;
    for (const key of ["token", "accessToken"]) {
      const val = nested[key];
      if (typeof val === "string" && val.trim().length > 0) return val.trim();
    }
  }
  return null;
}

// Indica si una respuesta pertenece a una llamada API propia (para el 401)
export function isOwnApiResponse(urlStr: string): boolean {
  try {
    const pathname = new URL(urlStr, typeof window !== "undefined" ? window.location.origin : "http://localhost").pathname.toLowerCase();
    return pathname.startsWith("/api/v1/") || pathname.startsWith("/brand-api/");
  } catch {
    return false;
  }
}
export function installDevFetchInterceptor(): void {
  if (typeof window === "undefined" || window.__cors_proxy_patched) return;

  window.__cors_proxy_patched = true;
  const originalFetch = window.fetch;

  window.fetch = async function (input: RequestInfo | URL, init?: RequestInit) {
    const isDevHost =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname.endsWith(".lvh.me");

    const apiServers = getConfiguredApiServers();

    if (isDevHost) {
      if (typeof input === "string") {
        let inputStr = input;
        for (const s of apiServers) {
          if (inputStr.startsWith(`${s}/api/v1/`)) {
            inputStr = inputStr.replace(s, "");
            break;
          }
        }
        input = inputStr;
        const isApiCall = inputStr.startsWith("/api/v1/");
        if (isApiCall) {
          init = init || {};
          const headers = new Headers(init.headers || {});
          ensureJsonHeader(headers, init.method);
          withSavedAuth(headers, inputStr);
          if (init.body && typeof init.body === "string") {
            init.body = resolveTemplateVariablesInBody(init.body);
          }
          init.headers = headers;
        }
      } else if (input instanceof URL) {
        const targetUrl = input;
        const matchesServer = apiServers.some((s) => {
          try { return targetUrl.origin === new URL(s).origin; } catch { return false; }
        });
        let inputStr = targetUrl.href;
        if (matchesServer && targetUrl.pathname.startsWith("/api/v1/")) {
          inputStr = `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
          input = inputStr;
        }
        const isApiCall = targetUrl.pathname.startsWith("/api/v1/") || matchesServer;
        if (isApiCall) {
          init = init || {};
          const headers = new Headers(init.headers || {});
          ensureJsonHeader(headers, init.method);
          withSavedAuth(headers, targetUrl.pathname);
          if (init.body && typeof init.body === "string") {
            init.body = resolveTemplateVariablesInBody(init.body);
          }
          init.headers = headers;
        }
      } else if (typeof Request !== "undefined" && input instanceof Request) {
        const reqUrl = new URL(input.url);
        const matchesServer = apiServers.some((s) => {
          try { return reqUrl.origin === new URL(s).origin; } catch { return false; }
        });
        const isTarget = (matchesServer && reqUrl.pathname.startsWith("/api/v1/")) || reqUrl.pathname.startsWith("/api/v1/");

        if (isTarget) {
          const proxiedUrl = `${reqUrl.pathname}${reqUrl.search}${reqUrl.hash}`;
          const headers = new Headers(input.headers);
          ensureJsonHeader(headers, input.method);
          withSavedAuth(headers, reqUrl.pathname);

          let finalBody: BodyInit | undefined = undefined;
          if (input.method !== "GET" && input.method !== "HEAD") {
            try {
              const text = await input.clone().text();
              if (text) {
                finalBody = resolveTemplateVariablesInBody(text);
              }
            } catch {
              const bodyBlob = await input.clone().blob();
              if (bodyBlob && bodyBlob.size > 0) finalBody = bodyBlob;
            }
          }

          input = new Request(proxiedUrl, {
            method: input.method,
            headers,
            body: finalBody,
            mode: "cors",
            credentials: input.credentials,
          });
        }
      }
    } else {
      if (typeof input === "string") {
        const proxied = toBrandProxyPath(input);
        if (proxied) {
          input = proxied;
          init = init || {};
          const headers = new Headers(init.headers || {});
          withSavedAuth(headers, new URL(proxied, window.location.origin).pathname);
          init.headers = headers;
        }
      } else if (input instanceof URL) {
        const proxied = toBrandProxyPath(input.href);
        if (proxied) {
          input = proxied;
          init = init || {};
          const headers = new Headers(init.headers || {});
          withSavedAuth(headers, new URL(proxied, window.location.origin).pathname);
          init.headers = headers;
        }
      } else if (typeof Request !== "undefined" && input instanceof Request) {
        const proxiedUrl = toBrandProxyPath(input.url);
        if (proxiedUrl) {
          let finalBody: BodyInit | undefined | null = undefined;
          if (input.method !== "GET" && input.method !== "HEAD") {
            try {
              const text = await input.clone().text();
              if (text) finalBody = text;
            } catch {
              try {
                const bodyBlob = await input.clone().blob();
                if (bodyBlob && bodyBlob.size > 0) finalBody = bodyBlob;
              } catch {
                //ignored
              }
            }
          }
          const headers = withSavedAuth(
            new Headers(input.headers),
            new URL(proxiedUrl, window.location.origin).pathname,
          );
          input = new Request(window.location.origin + proxiedUrl, {
            method: input.method,
            headers,
            body: finalBody,
            mode: "cors",
            credentials: input.credentials,
            signal: input.signal,
          });
        } else {
          try {
            const pathname = new URL(input.url, window.location.origin).pathname;
            if (
              pathname.startsWith("/brand-api/") ||
              pathname.startsWith("/api/v1/")
            ) {
              withSavedAuth(input.headers as Headers, pathname);
            }
          } catch {
            //ignored
          }
        }
      }
    }

    const response = await originalFetch.call(this, input, init);

    // Cierra sesión solo ante 401 de llamadas API propias
    const urlForAuth = typeof input === "string" ? input : input instanceof Request ? input.url : input.href;
    if (response.status === 401 && isOwnApiResponse(urlForAuth)) {
      clearAuthToken();
    }

    // Captura el token cuando el login responde OK
    try {
      const urlStr = urlForAuth;
      if (urlStr.toLowerCase().includes("/user/login") && response.ok) {
        const cloned = response.clone();
        cloned
          .json()
          .then((json) => {
            const tokenToSave = extractLoginToken(json);

            if (tokenToSave) {
              saveAuthToken(tokenToSave);
            }
          })
          .catch(() => {});
      }
    } catch {
      //ignored
    }

    return response;
  };
}

declare global {
  interface Window {
    __cors_proxy_patched?: boolean;
  }
}